// What the Cash and ledgers page reads (#486): `GET /api/cash`, as the person may see it. Amounts are in paise.
import { call, type Fetch } from "../http.js";

export type SensitiveDataClass = "costs_and_margins" | "payroll" | "bank_and_cash" | "customer_pricing";
export type AgeingBucket = "not_due" | "1_30" | "31_60" | "61_90" | "over_90";
export const AGEING_BUCKETS: readonly AgeingBucket[] = ["not_due", "1_30", "31_60", "61_90", "over_90"];

/** A part of the page the person's Roles hide, by the classes it also needs; or one LAMP could not read. */
export type Unshown =
  | { readonly status: "hidden"; readonly needs: readonly SensitiveDataClass[] }
  | { readonly status: "failed"; readonly problems: readonly string[] };

export interface OpenItem {
  readonly id: string;
  readonly number: string;
  readonly counterparty: string;
  readonly date: string;
  readonly dueDate: string;
  readonly outstanding: number;
  readonly daysOverdue: number;
  readonly bucket: AgeingBucket;
  readonly run: string | null;
}

export type Ageing =
  | {
      readonly status: "shown";
      readonly total: number;
      readonly buckets: Readonly<Record<AgeingBucket, number>>;
      readonly open: readonly OpenItem[];
    }
  | Unshown;

export interface ForecastWeek {
  readonly week: number;
  readonly from: string;
  readonly to: string;
  readonly opening: number;
  readonly receipts: number;
  readonly payments: number;
  readonly closing: number;
}

export interface CashForecast {
  readonly from: string;
  readonly to: string;
  readonly opening: number;
  readonly weeks: readonly ForecastWeek[];
  readonly negativeWeeks: readonly number[];
}

export interface LedgerEntry {
  readonly posting: string;
  readonly date: string;
  /** Debits positive, credits negative. */
  readonly amount: number;
  readonly document: { readonly object: string; readonly id: string; readonly number: string | null };
  readonly counterparty: string | null;
  readonly run: string | null;
}

export interface LedgerAccount {
  readonly account: string;
  readonly name: string;
  readonly class: string;
  /** Debits positive, credits negative. */
  readonly balance: number;
  readonly count: number;
  readonly entries: readonly LedgerEntry[];
}

/**
 * The bank's opening balance from the business's books (#1004): given, and the bank's balance starts from it; or not
 * given, with the date LAMP's postings to the bank begin, and the bank's balance is only what LAMP posted.
 */
export type BankOpening =
  | {
      readonly status: "given";
      readonly amount: number;
      readonly asOf: string | null;
      readonly source: "tally" | "given";
      readonly ledger: string | null;
      readonly posted: number;
      readonly mayGive: boolean;
    }
  | { readonly status: "not_given"; readonly since: string | null; readonly mayGive: boolean };

/** What the stock on hand is worth today, in paise, and how many Items held are not valued for want of a cost. */
export interface StockWorth {
  readonly value: number;
  readonly items: number;
  readonly unvalued: number;
}

export interface CashPage {
  readonly asOf: string;
  readonly balances: readonly { readonly account: string; readonly name: string; readonly amount: number }[];
  readonly bankOpening: BankOpening;
  readonly receivables: Ageing;
  readonly payables: Ageing;
  /** Not drawn while the bank's opening balance is not given (#1004): it would open on LAMP's postings alone. */
  readonly forecast:
    | { readonly status: "shown"; readonly forecast: CashForecast }
    | { readonly status: "not_given"; readonly since: string | null }
    | Unshown;
  readonly ledgers: { readonly status: "shown"; readonly accounts: readonly LedgerAccount[] } | Unshown;
  /** The stock Ledger's balance (#1013): the stock on hand at weighted average purchase cost. */
  readonly stock: { readonly status: "shown"; readonly stock: StockWorth } | Unshown;
  readonly empty: boolean;
}

export interface CashApi {
  cash(): Promise<CashPage>;
  /** `POST /api/cash/bank-opening`: the bank's opening balance in paise, and the date it is at. */
  giveBankOpening(amount: number, asOf: string): Promise<unknown>;
}

export const createCashApi = (fetcher: Fetch = (...args) => fetch(...args)): CashApi => ({
  cash: () => call<CashPage>(fetcher, "/api/cash"),
  giveBankOpening: (amount, asOf) => call(fetcher, "/api/cash/bank-opening", { json: { amount, asOf } }),
});
